import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Devices,
  Webhook,
} from '@mui/icons-material';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../contexts/AuthContext';

const DashboardPage = () => {
  const { user } = useAuth();
  // Datos de ejemplo para las métricas
  const metrics = [
    {
      title: 'Webhooks Activos',
      value: '12',
      change: '+2.1%',
      icon: <Webhook />,
      positive: true,
    },
    {
      title: 'Dispositivos',
      value: '8',
      change: '+5.4%',
      icon: <Devices />,
      positive: true,
    },
    {
      title: 'Contactos',
      value: '24',
      change: '+12.5%',
      icon: <People />,
      positive: true,
    },
    {
      title: 'Mensajes Hoy',
      value: '156',
      change: '+8.2%',
      icon: <TrendingUp />,
      positive: true,
    },
  ];

  return (
    <AppLayout currentPage="dashboard">
      <Box>
        {/* Título de bienvenida */}
        <Box sx={{ marginBottom: { xs: 3, sm: 4 } }}>
          <Typography
            variant="h4"
            sx={{
              color: '#111827',
              fontWeight: 600,
              marginBottom: 1,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
            }}
          >
            ¡Bienvenido de nuevo, {user?.nombre?.split(' ')[0] || 'Usuario'}!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#6b7280',
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            Aquí tienes un resumen de tu actividad reciente.
          </Typography>
        </Box>

        {/* Tarjetas de métricas */}
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ marginBottom: { xs: 3, sm: 4 } }}>
          {metrics.map((metric, index) => (
            <Grid item xs={6} sm={6} lg={3} key={index}>
              <Card
                sx={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.2s ease',
                  height: 'fit-content',
                  '&:hover': {
                    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <CardContent sx={{ padding: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    minHeight: { xs: '80px', sm: '90px' },
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#6b7280',
                          marginBottom: { xs: 0.5, sm: 0.75 },
                          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                          fontWeight: 500,
                        }}
                      >
                        {metric.title}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          color: '#111827',
                          fontWeight: 700,
                          marginBottom: { xs: 0.25, sm: 0.5 },
                          fontSize: { xs: '1.75rem', sm: '2rem' },
                          lineHeight: 1.1,
                        }}
                      >
                        {metric.value}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: metric.positive ? '#10b981' : '#ef4444',
                          fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                          fontWeight: 600,
                        }}
                      >
                        {metric.change}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        padding: { xs: 1, sm: 1.25 },
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        color: '#64748b',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: { xs: '36px', sm: '40px' },
                        minHeight: { xs: '36px', sm: '40px' },
                      }}
                    >
                      {metric.icon}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Sección de acceso rápido */}
        <Card
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h6"
              sx={{
                color: '#111827',
                fontWeight: 600,
                marginBottom: 2,
              }}
            >
              Acceso Rápido
            </Typography>
            
            <Grid container spacing={2}>
              {[
                { icon: <Webhook />, text: 'Crear Webhook' },
                { icon: <Devices />, text: 'Agregar Dispositivo' },
                { icon: <People />, text: 'Nuevo Contacto' },
                { icon: <TrendingUp />, text: 'Ver Reportes' }
              ].map((item, index) => (
                <Grid item xs={6} sm={6} md={3} key={index}>
                  <Box
                    sx={{
                      padding: { xs: 1.5, sm: 2 },
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                    }}
                  >
                    <Box sx={{ color: '#6b7280', marginBottom: { xs: 0.5, sm: 1 } }}>
                      {item.icon}
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#374151', 
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2,
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </AppLayout>
  );
};

export default DashboardPage; 